import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { gatorProtectActions } from '@/redux/modules/gatorProtect';
import {
  PlansBanner,
  ProtectionCardFactory,
  Summary,
} from './components';

import ChartSection from './sections/ChartSection/ChartSection';
import ThreatsTableSection from './sections/ThreatsTableSection/ThreatsTableSection';
import * as Styles from './GatorProtectHandler.styles';

import useLocale from '@/hooks/useLocale';
import withErrorBoundary from '@/utils/errorBoundary';
import ActiveTrialModal from './components/ActiveTrialModal';
import ActivateProModal from './components/ActivateProModal';
import { getActiveProModalCopy } from './components/ActivateProModal/getActiveProModalCopy';
import UpgradeCycleModal from './components/UpgradeCycleModal/UpgradeCycleModal';
import { productsActions } from '@/redux/modules/products';
import { GATOR_PROTECT_ID } from './GatorProtect.enum';
import { parseGatorProtectUpgradeHostingId } from '@/redux/modules/gatorProtect/gatorProtect.buy.utils';
import { parseDiscountPercent, useGatorProtectProPlan } from './hooks/useGatorProtectProPlan';
import { getIsoDateStringDaysAgo } from '@/utils/date-time/getIsoDateStringDaysAgo';
import { useHistory, useParams } from 'react-router';
import { getSelectedPlanInfo } from './utils/getSelectedPlanInfo';

import { useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  MONETIZACAO_GATOR_PROTECT_TRIAL_AVAILABLE,
  MONETIZACAO_MODAL_UPGRADE_GATOR_PROTECT_CYCLE,
  MONETIZACAO_SHOW_EXPORT_GATORPROTECT_BUTTON,
  MONETIZACAO_THREATS_TABLE_SECTION,
} from '@/config/GrowthBook/constants';
import { loadCreditCard } from '@/redux/actions/creditCard';
import { WHMCS_URL } from '@/config';

const TRIAL_ACTIVATE = 'TrialActivate';
const PRO_ACTIVATE = 'ProActivate';

/**
 * Deep link: `/:productId` (route param) pre-selects the hosting
 * when opening the panel (e.g. threats alert from home).
 */
const GatorProtectHandler = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    gatorProtect: gatorProtectLocale,
    routes: routesLocale,
  } = useLocale();
  const params = useParams();
  const { loading: gatorProtectLoading, plans, planDetails } = useSelector(state => state.gatorProtect);
  const { loading: fastCheckoutLoading } = useSelector(state => state.fastCheckout);
  const enableTrial = useFeatureIsOn(MONETIZACAO_GATOR_PROTECT_TRIAL_AVAILABLE);
  const [selectedPlan, setSelectedPlan] = useState(null);
  // fundo branco, gator a esquerda
  const [trialModalOpen, setTrialModalOpen] = useState(false);
  // fundo branco se for turbo, se não, fundo azul select de cycle
  const [proModalOpen, setProModalOpen] = useState(false);
  // fundo azul, select de cycle
  const [upgradeCycleModalOpen, setUpgradeCycleModalOpen] = useState(false);
  const {
    monthly,
    annual,
    triennial,
    availableCycles,
    isLoadingPlanData,
  } = useGatorProtectProPlan();

  const summaryState = useSelector(state => state.summary);
  const creditCard = useSelector(state => state.creditCard);
  const withThreatsTable = useFeatureIsOn(MONETIZACAO_THREATS_TABLE_SECTION);
  const withExportButton = useFeatureIsOn(MONETIZACAO_SHOW_EXPORT_GATORPROTECT_BUTTON);
  const isUpgradeCycleModalEnabled = useFeatureIsOn(MONETIZACAO_MODAL_UPGRADE_GATOR_PROTECT_CYCLE);

  const loading = gatorProtectLoading || creditCard?.loading;
  const forceOpenPaymentOptions = '?openInvoice=true';

  const {
    isTrialActive,
    isTrialExpired,
    isProActive,
    isProOnMonthlyOrAnnualCycle,
    isTriennialHostingCycle,
    isAAClient,
    hasClientUsedTrial,
    isTurbo,
    invoiceId,
    isAnnualHostingCycle,
  } = getSelectedPlanInfo(selectedPlan);

  const isTrialEligibleForUpgradeCycleModal = isTrialActive || isTrialExpired;

  const canOpenUpgradeCycleModal = isUpgradeCycleModalEnabled
    && (isProActive || isTrialEligibleForUpgradeCycleModal);

  const protectionCardUpgradeCycleDiscountPercent = parseDiscountPercent(
    isAnnualHostingCycle ? triennial?.discountPercent : annual?.discountPercent,
  );

  const invoiceHref = `${routesLocale.invoices}${routesLocale.unpaid}/${invoiceId}${forceOpenPaymentOptions}`;

  useEffect(() => {
    dispatch(productsActions.products.getProductById(GATOR_PROTECT_ID.PRO));
  }, [dispatch]);

  useEffect(() => {
    dispatch(loadCreditCard());
  }, [dispatch]);

  useEffect(() => {
    dispatch(gatorProtectActions.plans.request({ headerId: null }));
  }, [dispatch]);

  useEffect(() => {
    if (!summaryState?.features?.gator_protect) {
      history.push(routesLocale.home);
    }
  }, [history, routesLocale, summaryState]);

  useEffect(() => {
    if (!selectedPlan?.shared_hosting_id) {
      return;
    }
    dispatch(gatorProtectActions.planThreats.request({
      hostingId: selectedPlan.shared_hosting_id,
      page: 1,
      perPage: 10,
    }));
  }, [dispatch, selectedPlan?.shared_hosting_id]);

  const requestPlanThreatsList = (page) => {
    if (!selectedPlan?.shared_hosting_id) {
      return;
    }
    dispatch(gatorProtectActions.planThreats.request({
      hostingId: selectedPlan.shared_hosting_id,
      page: page || 1,
      perPage: 10,
    }));
  };

  const handleOnChangePlan = (plan) => {
    setSelectedPlan(plan);
    const formattedStartDate = getIsoDateStringDaysAgo(30);

    dispatch(gatorProtectActions.planDetails.request({
      hostingId: plan.shared_hosting_id,
      startDate: formattedStartDate,
    }));
  };

  const closeTrialModal = () => {
    setTrialModalOpen(false);
  };

  const closeProModal = () => {
    setProModalOpen(false);
  };

  const openProtectionProModal = () => {
    setTrialModalOpen(false);
    setProModalOpen(true);
  };

  const openProtectionTrialModal = () => {
    setProModalOpen(false);
    setTrialModalOpen(true);
  };

  const openUpgradeCycleModal = () => {
    if (!canOpenUpgradeCycleModal) {
      return;
    }
    setTrialModalOpen(false);
    setProModalOpen(false);
    setUpgradeCycleModalOpen(true);
  };

  const closeUpgradeCycleModal = () => {
    setUpgradeCycleModalOpen(false);
  };

  const openCustomUpgrade = () => {
    const id = parseGatorProtectUpgradeHostingId(selectedPlan?.gatorprotect_hosting_id);
    if (id == null) {
      return;
    }
    window.open(`${WHMCS_URL}/index.php?m=custom_upgrade&action=selectupgrade&id=${id}`);
  };

  const upgradeCycleModalContent = useMemo(() => {
    const base = gatorProtectLocale?.summary?.upgradeCycleModal;
    if (!base) {
      return undefined;
    }
    const { fromAnnualHosting, ...rest } = base;
    if (isAnnualHostingCycle && fromAnnualHosting) {
      return { ...rest, ...fromAnnualHosting };
    }
    return rest;
  }, [gatorProtectLocale?.summary?.upgradeCycleModal, isAnnualHostingCycle]);

  const upgradeCyclesForModal = useMemo(() => {
    if (!availableCycles?.length) {
      return [];
    }

    const hasRecognizedProBillingCycle = isProOnMonthlyOrAnnualCycle || isTriennialHostingCycle;

    if (!isProActive || !hasRecognizedProBillingCycle) {
      return availableCycles;
    }

    if (isTriennialHostingCycle) {
      return [];
    }

    if (isAnnualHostingCycle) {
      return availableCycles.filter(cycleKey => cycleKey === 'triennially');
    }

    return availableCycles.filter(
      cycleKey => cycleKey === 'annually' || cycleKey === 'triennially',
    );
  }, [
    availableCycles,
    isProActive,
    isProOnMonthlyOrAnnualCycle,
    isAnnualHostingCycle,
    isTriennialHostingCycle,
  ]);

  const upgradeCycleOptions = useMemo(() => {
    if (!upgradeCycleModalContent || !upgradeCyclesForModal.length) {
      return [];
    }
    const resolvePlan = (cycleKey) => {
      if (cycleKey === 'monthly') return monthly;
      if (cycleKey === 'annually') return annual;
      return triennial;
    };
    const resolveBaseLabel = (cycleKey) => {
      if (cycleKey === 'monthly') return upgradeCycleModalContent.paymentCycleLabelMonthly;
      if (cycleKey === 'annually') return upgradeCycleModalContent.paymentCycleLabelAnnual;
      return upgradeCycleModalContent.paymentCycleLabelTriennial;
    };
    return upgradeCyclesForModal.map((cycleKey) => {
      const plan = resolvePlan(cycleKey);
      if (!plan) {
        return null;
      }
      const baseLabel = resolveBaseLabel(cycleKey);
      const label = plan.isBestOption === true
        ? `${baseLabel} | ${upgradeCycleModalContent.bestOptionSuffix}`
        : baseLabel;
      const tag = plan.discountPercent > 0 ? upgradeCycleModalContent.getDiscountBadge(plan.discountPercent) : undefined;
      return {
        cycleKey,
        label,
        tag,
        formattedPrice: plan.formattedPrice,
        discountPercent: plan.discountPercent,
      };
    }).filter(Boolean);
  }, [
    upgradeCycleModalContent,
    upgradeCyclesForModal,
    monthly,
    annual,
    triennial,
  ]);

  const proActivationUpgradeCycleContent = useMemo(() => {
    if (!proModalOpen || !isUpgradeCycleModalEnabled) {
      return null;
    }

    const copy = getActiveProModalCopy(gatorProtectLocale?.activeProModal, isTurbo);
    const upgrade = upgradeCycleModalContent;

    if (!copy || !upgrade) {
      return null;
    }

    const staticCtaLabel = isTurbo && typeof copy.buttonLabel === 'string'
      ? copy.buttonLabel
      : undefined;

    return {
      heroTitle: copy.title,
      heroSubtitle: copy.subtitle,
      featureItems: copy.checklistItems,
      paymentCycleLabel: upgrade.paymentCycleLabel,
      selectCyclePlaceholder: upgrade.selectCyclePlaceholder,
      paymentCycleLabelMonthly: upgrade.paymentCycleLabelMonthly,
      paymentCycleLabelAnnual: upgrade.paymentCycleLabelAnnual,
      paymentCycleLabelTriennial: upgrade.paymentCycleLabelTriennial,
      bestOptionSuffix: upgrade.bestOptionSuffix,
      getDiscountBadge: upgrade.getDiscountBadge,
      termsPrefix: isTurbo ? copy.termsPrefix : upgrade.termsPrefix,
      termsLink: isTurbo ? copy.termsLink : upgrade.termsLink,
      getCtaLabel: upgrade.getCtaLabel,
      refundNote: upgrade.refundNote,
      staticCtaLabel,
      ctaPriceFallback: monthly?.formattedPrice ?? '...',
    };
  }, [
    proModalOpen,
    isUpgradeCycleModalEnabled,
    gatorProtectLocale?.activeProModal,
    isTurbo,
    upgradeCycleModalContent,
    monthly?.formattedPrice,
  ]);

  const handleAutomaticCleaningToggle = () => {
    const current = planDetails.auto_protect ?? true;
    const next = !current;
    dispatch(gatorProtectActions.planDetails.setAutoProtect(next));
    if (selectedPlan?.shared_hosting_id != null) {
      dispatch(gatorProtectActions.planDetails.updateAutoProtect.request({
        hostingId: selectedPlan.shared_hosting_id,
        autoProtect: next,
      }));
    }
  };

  useEffect(() => {
    if (plans.length === 0 || selectedPlan !== null) {
      return;
    }

    const planFromRoute = params?.productId
      ? plans.find(p => p.shared_hosting_id === Number(params.productId))
      : null;
    const plan = planFromRoute ?? plans[0];
    setSelectedPlan(plan);
    const formattedStartDate = getIsoDateStringDaysAgo(30);
    dispatch(gatorProtectActions.planDetails.request({
      hostingId: plan.shared_hosting_id,
      startDate: formattedStartDate,
    }));
  }, [plans, selectedPlan, dispatch, params?.productId]);

  const activateProtectionPro = (billingCycle = 'monthly') => {
    const hostingId = selectedPlan?.shared_hosting_id;
    if (hostingId != null) {
      dispatch(gatorProtectActions.acceptTerms.request({
        hostingId,
        termsAccepted: PRO_ACTIVATE,
      }));
    }

    const redirectToUnpaidInvoice = (invoiceId) => {
      history.push(`${routesLocale.invoices}${routesLocale.unpaid}/${invoiceId}${forceOpenPaymentOptions}`);
    };

    dispatch(gatorProtectActions.purchase.fastCheckout.request({
      productId: GATOR_PROTECT_ID.PRO,
      domain: selectedPlan?.shared_hosting_domain,
      redirectWithInvoice: redirectToUnpaidInvoice,
      hostingId: selectedPlan?.shared_hosting_id,
      billingCycle,
    }));
  };

  const handleUpgradeCycleModalConfirm = () => {
    closeUpgradeCycleModal();

    openCustomUpgrade();
  };


  const handleProtectionCardActivatePro = () => {
    const shouldOpenUpgradeCycleModalOnActivatePro = Boolean(
      isUpgradeCycleModalEnabled && isTrialEligibleForUpgradeCycleModal,
    );
    if (shouldOpenUpgradeCycleModalOnActivatePro) {
      openUpgradeCycleModal();
      return;
    }
    openProtectionProModal();
  };

  const activateTrial = () => {
    if (!enableTrial) {
      return;
    }

    const hostingId = selectedPlan?.shared_hosting_id;
    if (hostingId != null) {
      dispatch(gatorProtectActions.acceptTerms.request({
        hostingId,
        termsAccepted: TRIAL_ACTIVATE,
      }));
    }

    dispatch(gatorProtectActions.purchase.fastCheckout.request({
      productId: GATOR_PROTECT_ID.TRIAL,
      domain: selectedPlan?.shared_hosting_domain,
      hostingId: selectedPlan?.shared_hosting_id,
      redirectWithInvoice: false,
      onPurchaseSuccess: () => {
        setSelectedPlan({
          ...selectedPlan,
          is_trial: true,
          expire_days: isAAClient ? 365 : 30,
        });
        setTrialModalOpen(false);
        dispatch(gatorProtectActions.plans.request({ headerId: null }));
        const formattedStartDate = getIsoDateStringDaysAgo(30);
        dispatch(gatorProtectActions.planDetails.request({
          hostingId: selectedPlan.shared_hosting_id,
          startDate: formattedStartDate,
        }));
      },
    }));
  };

  const sortedChartData = useMemo(() => {
    if (!planDetails.activity_detected) {
      return [];
    }
    const compareByDateAscending = (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime();
    return [...planDetails.activity_detected].sort(compareByDateAscending);
  }, [planDetails.activity_detected]);

  return (
    <Styles.Container data-testid="gator-protect-handler">
      <div>
        <PlansBanner
          content={gatorProtectLocale.banner}
          plans={plans}
          loading={loading || plans.length === 0}
          selectedPlan={selectedPlan}
          onChangePlan={handleOnChangePlan}
        />
        <Summary content={gatorProtectLocale.summary} loading={loading}>
          {({ Banners, Main, Alerts }) => (
            <>
              <Banners>
                {isTrialActive && <Summary.Banner type="trialActive" trialDaysLeft={(selectedPlan && selectedPlan.expire_days) || 0} />}
                {isProActive && (
                  <Summary.Banner
                    type="fullProtectionActive"
                    onFullProtectionCtaClick={isUpgradeCycleModalEnabled ? openUpgradeCycleModal : undefined}
                  />
                )}
                {isTrialExpired && <Summary.Banner type="expiredProtection" />}
              </Banners>
              <Main>
                <Summary.Title loading={loading} />

                <Styles.DataAndMetricsRow autoColumns={isProActive}>
                  <Summary.Metrics
                    threatsIdentified={planDetails?.summary?.detected}
                    threatsRemediated={planDetails?.summary?.remediated}
                    enabled={(isProActive || isTrialActive) ? (planDetails.auto_protect ?? true) : undefined}
                    onToggle={(isProActive || isTrialActive) ? handleAutomaticCleaningToggle : undefined}
                    lastScan={planDetails?.summary?.last_scan}
                    hasToggle={isProActive || isTrialActive}
                  />

                  <ProtectionCardFactory
                    isLoading={loading}
                    selectedPlan={selectedPlan}
                    isUpgradeCycleModalEnabled={isUpgradeCycleModalEnabled}
                    onActivateProtectionPro={handleProtectionCardActivatePro}
                    onActivateTrial={openProtectionTrialModal}
                    onPendingInvoiceClick={() => history.push(invoiceHref)}
                    isAAClient={isAAClient}
                    hideActivateTrial={isTrialActive || hasClientUsedTrial}
                    isTurbo={isTurbo}
                    enableTrial={enableTrial}
                    upgradeCycleDiscountPercent={protectionCardUpgradeCycleDiscountPercent}
                    upgradeCyclePricingLoading={isLoadingPlanData}
                    onUpgradeCycleClick={openUpgradeCycleModal}
                  />
                </Styles.DataAndMetricsRow>
              </Main>
              <Alerts />
            </>
          )}
        </Summary>
      </div>

      <ChartSection
        sectionId="activities-chart"
        data={sortedChartData}
        isLoading={loading}
      />

      {withThreatsTable && (
        <ThreatsTableSection
          requestPlanThreatsList={requestPlanThreatsList}
          showExportButton={withExportButton}
        />
      )}

      {trialModalOpen && (
        <ActiveTrialModal
          open
          selectedPlan={selectedPlan}
          isTrialActive={isTrialActive}
          isTrialExpired={isTrialExpired}
          isAAClient={isAAClient}
          monthly={monthly}
          onActivate={activateTrial}
          onClose={closeTrialModal}
          loadingData={loading || isLoadingPlanData}
          fastCheckoutLoading={fastCheckoutLoading}
        />
      )}

      {upgradeCycleModalOpen && canOpenUpgradeCycleModal && (
        <UpgradeCycleModal
          content={upgradeCycleModalContent}
          upgradeCycleOptions={upgradeCycleOptions}
          loading={loading || isLoadingPlanData || fastCheckoutLoading}
          onClose={closeUpgradeCycleModal}
          onConfirm={handleUpgradeCycleModalConfirm}
        />
      )}

      {(proModalOpen && isUpgradeCycleModalEnabled && proActivationUpgradeCycleContent && !isTurbo) && (
        <UpgradeCycleModal
          variant="activation"
          testId="gator-protect-protection-modal"
          showProBadge
          content={proActivationUpgradeCycleContent}
          upgradeCycleOptions={upgradeCycleOptions}
          loading={loading || isLoadingPlanData || fastCheckoutLoading}
          onClose={closeProModal}
          onConfirm={activateProtectionPro}
        />
      )}

      {proModalOpen && (!isUpgradeCycleModalEnabled || isTurbo) && (
        <ActivateProModal
          open
          isTurbo={isTurbo}
          monthly={monthly}
          onActivate={activateProtectionPro}
          onClose={closeProModal}
          loading={loading || isLoadingPlanData}
          fastCheckoutLoading={fastCheckoutLoading}
        />
      )}
    </Styles.Container>
  );
};

export default withErrorBoundary(GatorProtectHandler, 'GatorProtectHandler');
